import { ConditionalLink } from "components/conditionalLink";
import HoverCard from "components/DesignSystem/HoverCard/HoverCard";
import { Tick } from "components/icons/Tick";
import { APP_SWITCHER_CLICKED } from "constants/analyticsEvents";
import { App, useCurrentAppContext } from "contexts/CurrentAppContext";
import { useAnalytics } from "hooks/useAnalytics";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useWindowSize } from "hooks/useWindowSize";
import authContext from "jwt_context&axios/authContext";
import { ReactNode, useContext, useRef, useState } from "react";
import { Timeout } from "react-number-format/types/types";
import { useRouteMatch } from "react-router-dom";

export const AppNavigation = ({ children }: { children: ReactNode }) => {
  const { link } = useConstructInternalLink();
  const { path } = useRouteMatch();
  const { isForeignCA } = useRoleBasedView();
  const [pop, setPop] = useState(false);
  const closeRef = useRef<Timeout | null>(null);
  const { trackEvent } = useAnalytics();
  const {
    authtoken: { email },
  } = useContext(authContext);

  const { appList } = useCurrentAppContext();
  const { isMobile } = useWindowSize();
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const onClose = () => {
    if (closeRef.current) {
      clearTimeout(closeRef.current);
    }

    closeRef.current = setTimeout(() => {
      setPop(false);
    }, 200);
  };

  const onOpen = () => {
    closeRef.current && clearTimeout(closeRef.current);
    setPop(true);
  };

  // Hover cards don't open on touch, so on mobile a tap toggles the menu.
  // Radix's trigger cancels touchstart, which suppresses the follow-up click
  // on phones, so the toggle runs on pointerdown instead.
  const handleTriggerPointerDown = (e: React.PointerEvent) => {
    if (!isMobile) return;

    e.preventDefault();
    setPop((prev) => !prev);
  };

  const handleTriggerClick = (e: React.MouseEvent) => e.preventDefault();

  // Tapping the trigger counts as an outside tap for the card; let the
  // trigger's click toggle it instead of closing and reopening.
  const keepOpenOnTriggerTap = (e: Event) => {
    const { target } = e;

    if (target instanceof Node && triggerRef.current?.contains(target)) {
      e.preventDefault();
    }
  };

  const handleClick = ({
    redirectUrl,
    name,
  }: Pick<App, "redirectUrl" | "name">) => {
    setPop(false);
    trackEvent(APP_SWITCHER_CLICKED, {
      app_url: redirectUrl,
      app_name: name,
      email,
    });
  };

  if (isForeignCA) {
    return children;
  }

  return (
    <HoverCard.Root
      open={pop}
      openDelay={300}
      onOpenChange={isMobile ? setPop : undefined}
    >
      <HoverCard.Trigger
        asChild
        onClick={handleTriggerClick}
        onPointerDown={handleTriggerPointerDown}
        onMouseEnter={isMobile ? undefined : onOpen}
        onMouseLeave={isMobile ? undefined : onClose}
      >
        <div ref={triggerRef}>{children}</div>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          className="t-min-w-[230px] t-flex t-flex-col t-gap-1 t-p-1.5"
          align="start"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onPointerDownOutside={keepOpenOnTriggerTap}
          side="bottom"
          sideOffset={5}
        >
          {appList.map(({ name, AppSymbol, redirectUrl }) => {
            const isSelected = Boolean(path.includes(redirectUrl));

            return (
              <ConditionalLink
                to={link(redirectUrl)}
                key={name}
                onClick={() => handleClick({ redirectUrl, name })}
                className="all:unset t-flex t-justify-between t-items-center t-group t-w-full t-cursor-pointer t-truncate !t-border-none t-px-3 t-py-2 hover:t-bg-surface-grey focus-visible:t-outline-none data-[highlighted]:t-bg-surface-lighter-grey t-gap-1.5 group-data-state-open:t-text-purple t-rounded t-text-text-black"
              >
                <div className="t-flex t-w-full t-items-center t-gap-3">
                  <AppSymbol />
                  {name}
                </div>
                {isSelected && <Tick className="t-text-purple" />}
              </ConditionalLink>
            );
          })}
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};
